// MissionReady Radio — live transcription leg.
//
// Reuses the existing Deepgram integration (Nova-3) and the existing
// server-side credential handling: the browser only ever holds the short-lived
// grant token minted by `startRadioSession`. Transcription runs ALONGSIDE the
// WebRTC audio transport — Deepgram is never the transport. A transcription
// failure must never terminate the live Radio transmission.

const DG_URL =
  "wss://api.deepgram.com/v1/listen?model=nova-3&smart_format=true&punctuate=true&interim_results=true&encoding=linear16&sample_rate=16000&channels=1";

type Handlers = {
  onInterim: (text: string) => void;
  onFinal: (text: string) => void;
  onUnavailable: (reason: string) => void;
};

export class RadioTranscriber {
  private ws: WebSocket | null = null;
  private recorder: MediaRecorder | null = null;
  private audio: { ctx: AudioContext; src: MediaStreamAudioSourceNode; proc: ScriptProcessorNode } | null = null;

  private stopAudio() {
    const a = this.audio;
    this.audio = null;
    if (!a) return;
    try {
      a.proc.disconnect();
      a.src.disconnect();
      void a.ctx.close();
    } catch {
      /* ignore */
    }
  }
  private closed = false;

  constructor(private handlers: Handlers) {}

  get active() {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /** One transcription stream per transmission; calling twice is a no-op. */
  async start(stream: MediaStream, token: string | null): Promise<void> {
    if (this.ws || this.audio) return;
    if (!token) {
      this.handlers.onUnavailable("Live transcription unavailable");
      return;
    }
    if (typeof WebSocket === "undefined" || typeof window === "undefined") {
      this.handlers.onUnavailable("Live transcription unavailable");
      return;
    }
    this.closed = false;
    let ws: WebSocket;
    try {
      // Grant tokens are JWTs → "bearer" subprotocol (API keys use "token").
      ws = new WebSocket(DG_URL, ["bearer", token]);
    } catch {
      this.handlers.onUnavailable("Live transcription unavailable");
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      if (this.closed) return;
      try {
        // Raw 16 kHz PCM works in every browser, including iPhone Safari,
        // where MediaRecorder cannot produce webm/opus.
        const AC = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
        const ctx = new AC();
        void ctx.resume().catch(() => {});
        const src = ctx.createMediaStreamSource(stream);
        const proc = ctx.createScriptProcessor(4096, 1, 1);
        const ratio = ctx.sampleRate / 16000;
        proc.onaudioprocess = (e) => {
          if (ws.readyState !== WebSocket.OPEN) return;
          const input = e.inputBuffer.getChannelData(0);
          const len = Math.floor(input.length / ratio);
          const out = new Int16Array(len);
          for (let i = 0; i < len; i++) {
            const v = Math.max(-1, Math.min(1, input[Math.floor(i * ratio)]));
            out[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
          }
          ws.send(out.buffer);
        };
        src.connect(proc);
        proc.connect(ctx.destination);
        this.audio = { ctx, src, proc };
      } catch {
        this.handlers.onUnavailable("Live transcription unavailable");
        this.closeSocket();
      }
    };

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(String(ev.data));
        const text: string = msg?.channel?.alternatives?.[0]?.transcript ?? "";
        if (!text) return;
        if (msg.is_final) this.handlers.onFinal(text);
        else this.handlers.onInterim(text);
      } catch {
        /* ignore malformed frames */
      }
    };

    ws.onerror = () => {
      if (!this.closed) this.handlers.onUnavailable("Live transcription unavailable");
    };
    ws.onclose = () => {
      this.stopAudio();
      this.ws = null;
    };
  }

  /** Flush + close; resolves once Deepgram has had a moment to send finals. */
  async stop(graceMs = 900): Promise<void> {
    this.closed = true;
    try {
      if (this.recorder && this.recorder.state !== "inactive") this.recorder.stop();
    } catch {
      /* ignore */
    }
    this.recorder = null;
    this.stopAudio();
    const ws = this.ws;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify({ type: "CloseStream" }));
      } catch {
        /* ignore */
      }
      await new Promise<void>((resolve) => {
        const done = () => resolve();
        const timer = setTimeout(done, graceMs);
        ws.addEventListener("close", () => {
          clearTimeout(timer);
          done();
        });
      });
    }
    this.closeSocket();
  }

  private closeSocket() {
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
    this.ws = null;
  }
}
